import { NavLink } from "react-router";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { domainPath, type Domain } from "./nav";

/** The domain rail: light gray in light mode, navy in dark mode. On phones it becomes the bottom tab bar. */
export function Rail({ accountId, current, domains, variant = "side" }: { accountId?: string; current?: string; domains: Domain[]; variant?: "side" | "bottom" }) {
  const bottom = variant === "bottom";
  return (
    <nav
      aria-label="Domains"
      className={cn(
        "bg-rail text-rail-foreground",
        bottom ? "grid hairline-b" : "flex w-16 shrink-0 flex-col items-stretch border-r border-border py-1",
      )}
      style={bottom ? { gridTemplateColumns: `repeat(${domains.length}, minmax(0, 1fr))` } : undefined}
    >
      {domains.map((d) => {
        const active = current === d.id;
        const Icon = d.icon;
        const link = (
          <NavLink
            key={d.id}
            to={domainPath(d, accountId)}
            aria-label={d.label}
            aria-current={active ? "page" : undefined}
            className={cn(
              "relative flex flex-col items-center gap-0.5 px-1 py-2 text-[11px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-rail-active",
              active ? "bg-navigation-selection text-navigation-selection-foreground" : "text-rail-foreground/80 hover:bg-rail-foreground/5 hover:text-rail-foreground dark:hover:bg-white/10",
            )}
          >
            {active && <span aria-hidden="true" className={cn("pointer-events-none absolute bg-navigation-selection-foreground", bottom ? "inset-x-0 top-0 h-[3px]" : "inset-y-0 left-0 w-[3px]")} />}
            <span className="flex size-7 items-center justify-center">
              <Icon className="size-4" strokeWidth={1.75} />
            </span>
            <span>{d.label}</span>
          </NavLink>
        );
        if (bottom) return link;
        return (
          <Tooltip key={d.id} delayDuration={400}>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right">{d.label}</TooltipContent>
          </Tooltip>
        );
      })}
    </nav>
  );
}
